import { useUserContext } from '../../Contexts/UserConfig'
import { useMessagePopupContext } from '../../garvan-react/Components/ModalFeedback/MessagePopup'
import { useMutation, useQueries, useQuery, useQueryClient } from 'react-query'
import { AxiosResponse } from 'axios'
import SampleService from '../PHP/APIs/SampleService'
import {
  tsGetAnimalsInContainer,
  tsGetLinesInRack,
  tsWhoAmI,
} from '../../Typescript/API_Response'
import { useGlobalContext } from '../../Contexts/GlobalTriggers'
import { useAddSampleContext } from '../../Contexts/Modules/SampleModule/AddSampleModule'
import {
  tsAddAnimal,
  tsStorageAnimalMeta,
} from '../../Pages/Sample/AddAnimal/ValidSamplesState'
import { useNavigate } from 'react-router-dom'
import { RenderMessageInterface } from '../../garvan-react/Components/ModalFeedback/RenderMessage'
import { getUAI } from './ScreenHooks'
import PhpAPI from '../PHP/APIs/PhpAPI'

export function useSampleGet(
  path: string | undefined,
  enabled: boolean = true,
  options?: { onSuccess?: (data: any) => void },
) {
  const { whoAmI } = useUserContext()
  const { setError } = useMessagePopupContext()
  return useQuery<any, AxiosResponse>(
    ['SampleService', 'get', path],
    () => SampleService.get(whoAmI!, path!),
    {
      enabled: !!whoAmI && enabled,
      onError: (err) => setError(err),
      ...options,
    },
  )
}

async function sampleConfirm(whoAmI: tsWhoAmI, sampleIDs: string[]) {
  return SampleService.post(whoAmI!, '/newSamplesConfirm.php', {
    SampleIDs: sampleIDs,
  }).then((data) => !!data?.Success)
}

export function validPatternSampleID(sample_id: string) {
  return /^[A-z][A-z][0-9][0-9][0-9][0-9][0-9]$/.test(sample_id)
}

export async function validServerSampleID(
  whoAmI: tsWhoAmI | undefined,
  sample_id: string,
) {
  return SampleService.post(whoAmI!, `/getSamplesUAI.php?SampleID=${sample_id}`)
}

export function useValidateServerSampleID(setSampleID: any, setValidID: any) {
  const { whoAmI } = useUserContext()
  const { setMessage, setError } = useMessagePopupContext()
  return useMutation<any, AxiosResponse, string, () => void>(
    (sample_id) =>
      SampleService.post(whoAmI!, `/getSamplesUAI.php?SampleID=${sample_id}`),
    {
      onError: (err) => setError(err),
      onSuccess: async (data: any) => {
        if (data.UAI !== -1) {
          setSampleID('')
          setMessage({
            message: `This ID is already assigned to the animal with UAI ${data.UAI}`,
            subject: 'Not Unique',
            severity: 'warning',
          })
        } else if (data?.Message) {
          setSampleID('')
          setMessage({ subject: data.Message, severity: 'error' })
        } else {
          setValidID(true)
        }
      },
    },
  )
}

export function useSaveSamples(
  animals: tsAddAnimal,
  setAnimals: any,
  containerMeta: Partial<tsStorageAnimalMeta>,
  setContainerMeta: any,
) {
  const { whoAmI } = useUserContext()
  const { setError, setMessage } = useMessagePopupContext()
  const { setLoadingDialog } = useGlobalContext()
  const { collection, sampleRun } = useAddSampleContext()
  const animalCollection = collection as tsGetAnimalsInContainer
  const navigate = useNavigate()

  const samplesToSave: any[] = []
  Object.entries(animals)
    .filter(([_, samples]) => samples.length)
    .forEach(([uai, samples]) => {
      const animal = animalCollection.animals.find(
        ({ UAI }) => String(UAI) === uai,
      )
      return samples.forEach(({ sampleID, metadata }) => {
        samplesToSave.push({
          SampleID: sampleID,
          UAI: animal?.UAI,
          LineName: animal?.ShortLineName,
          AnimalID: animal?.AnimalID,
          SampleRunID: sampleRun?.runInfo?.SampleRunID,
          metadata: { ...metadata, ...containerMeta },
        })
      })
    })

  const body = {
    SampleRunID: sampleRun?.runInfo?.SampleRunID,
    OwnerID: animalCollection?.container?.OwnerID,
    HoldingID: sampleRun?.runInfo?.OriginHoldingID,
    samples: samplesToSave,
  }

  return useMutation<any, AxiosResponse>(
    () => {
      setLoadingDialog(true)
      return SampleService.post(whoAmI!, '/newSamples.php', body)
    },
    {
      onError: (err) => {
        setLoadingDialog(false)
        setError(err)
      },
      onSuccess: (data, variables, context) => {
        const errorMsg: RenderMessageInterface = {
          subject: data?.message ? data.message : 'Error confirming successful save',
          severity: 'error',
        }
        if (data?.SampleRunID) {
          sampleConfirm(
            whoAmI!,
            data?.samples.map((s: any) => s.SampleID),
          ).then((valid) => {
            if (!valid) {
              setMessage(errorMsg)
            } else {
              setAnimals({})
              setContainerMeta({})
              navigate(-1)
            }
          })
        } else {
          setMessage(errorMsg)
        }
        setLoadingDialog(false)
      },
    },
  )
}

export function useUpdateSampleResultsBulk(sample_ids: string[]) {
  const queryClient = useQueryClient()
  const { whoAmI } = useUserContext()
  const { setError, setMessage } = useMessagePopupContext()
  return useMutation<any, AxiosResponse, string, () => void>(
    ['SampleService', 'updateSampleResultsBulk', sample_ids],
    (result) =>
      SampleService.updateSampleResultsBulk(whoAmI!, result, sample_ids),
    {
      onSuccess: async (data) => {
        if (data?.message) {
          setMessage({
            subject: 'Unable to save results',
            message: data.message,
            severity: 'warning',
          })
        } else {
          await queryClient.invalidateQueries({
            queryKey: ['SampleService', 'get'],
          })
        }
      },
      onError: (err) => setError(err),
    },
  )
}

export function useNewScreenSample(setAnimals?: any) {
  const { whoAmI } = useUserContext()
  const { setError, setMessage } = useMessagePopupContext()
  const { sampleRun } = useAddSampleContext()
  const navigate = useNavigate()
  return useMutation<
    any,
    AxiosResponse,
    { SampleID: string; Description?: string; UAI: string[] },
    () => void
  >(
    async (fields) => {
      let newUAI = {}
      if (!fields?.UAI) {
        newUAI = { UAI: [1] } //await getUAI()}
      }
      return SampleService.post(whoAmI!, `/newScreenSample.php`, {
        ...fields,
        ...newUAI,
        SampleRunID: sampleRun?.runInfo.SampleRunID,
        OwnerID: 30,
        HoldingID: sampleRun?.runInfo.OriginHoldingID,
      })
    },
    {
      onSuccess: (data) => {
        const errorMsg: RenderMessageInterface = {
          subject: data?.message ? data.message : 'An error occurred saving sample',
          severity: 'error',
        }
        if (data?.SampleRunID) {
          sampleConfirm(whoAmI!, [data.SampleID]).then((valid) => {
            if (!valid) {
              setMessage(errorMsg)
            } else {
              if (setAnimals) {
                setAnimals({})
              }
              navigate(-1)
            }
          })
        } else {
          setMessage(errorMsg)
        }
      },
      onError: (err) => setError(err),
    },
  )
}

export function useNewEnvSample(sampleID: string, sampleDescription: string) {
  const { whoAmI } = useUserContext()
  const { setError, setMessage } = useMessagePopupContext()
  const { sampleRun } = useAddSampleContext()
  const { mutate: saveSample } = useNewScreenSample()
  return useMutation<any, AxiosResponse>(
    ['getUAI', sampleRun],
    () => getUAI(whoAmI!, sampleRun!),
    {
      onSuccess: (data) => {
        if (data?.UAI) {
          saveSample({
            SampleID: sampleID,
            Description: sampleDescription,
            UAI: [data.UAI],
          })
        } else {
          setMessage({
            severity: 'warning',
            subject: 'Unable to associate a UAI from current holding',
          })
        }
      },
      onError: (data) => setError(data),
    },
  )
}

export function useNewRackSample(
  sampleID: string,
  racksIDs: tsGetLinesInRack[],
) {
  const { whoAmI } = useUserContext()
  const { setError, setMessage } = useMessagePopupContext()
  const navigate = useNavigate()
  const { sampleRun } = useAddSampleContext()
  return useMutation<any, AxiosResponse>(
    ['getUAI', sampleRun],
    () =>
      SampleService.post(whoAmI!, `/newScreenSampleRack.php`, {
        SampleRunID: sampleRun?.runInfo.SampleRunID,
        SampleID: sampleID,
        HoldingID: sampleRun?.runInfo.OriginHoldingID,
        RackID: racksIDs,
      }),
    {
      onError: (data) => setError(data),
      onSuccess: (data) => {
        const errorMsg: RenderMessageInterface = {
          subject: data?.message ? data.message : 'An error occurred saving the rack sample',
          severity: 'error',
        }
        if (data?.SampleRunID) {
          sampleConfirm(whoAmI!, [data.SampleID]).then((valid) => {
            if (!valid) {
              setMessage(errorMsg)
            } else {
              navigate(-1)
            }
          })
        } else {
          setMessage(errorMsg)
        }
      },
    },
  )
}

export function useUpdateSampleResultComment(sample_id: string | undefined) {
  const queryClient = useQueryClient()
  const { whoAmI } = useUserContext()
  const { setError, setMessage } = useMessagePopupContext()
  return useMutation<any, AxiosResponse, string, () => void>(
    ['SampleService', 'updateSampleResultComment', sample_id],
    (comment) =>
      SampleService.updateSampleResultComment(whoAmI!, comment, sample_id!),
    {
      onSuccess: async (data) => {
        if (data?.message) {
          setMessage({
            subject: 'Unable to save result comment',
            message: data.message,
            severity: 'warning',
          })
        } else {
          await queryClient.invalidateQueries({
            queryKey: ['SampleService', 'getSample'],
          })
        }
      },
      onError: (err) => setError(err),
    },
  )
}

export function useGetLinesInRacks(rackIDs: string[], setRackIDs: any) {
  const { whoAmI } = useUserContext()
  const { setError, setMessage } = useMessagePopupContext()
  return useQueries(
    rackIDs.map((id) => {
      return {
        queryKey: ['getLinesInRack', id],
        queryFn: () =>
          PhpAPI.get(whoAmI, `/stuart/line/getLinesInRack.php?rackID=${id}`),
        onSuccess: (data: any) => {
          if (!data?.rack) {
            setMessage({
              subject: 'Rack not found',
              message: `Rack with ID:${id} could not be found or loaded`,
              severity: 'warning',
            })
            setRackIDs((ids: any[]) => ids.filter((k) => k !== id))
          }
        },
        onError: (err: AxiosResponse) => setError(err),
      }
    }),
  )
}

export function useGetAnimalsInContainers(
  containerIDs: string[],
  setContainerIDs: any,
) {
  const { whoAmI } = useUserContext()
  const { setError, setMessage } = useMessagePopupContext()
  return useQueries(
    containerIDs.map((id) => {
      return {
        queryKey: ['getAnimalsInContainer', id],
        queryFn: () =>
          PhpAPI.get(
            whoAmI,
            `/stuart/sample/getAnimalsInContainer.php?containerID=${id}`,
          ),
        onSuccess: (data: any) => {
          if (!data?.container) {
            setMessage({
              subject: 'Container not found',
              message: `Container with ID:${id} could not be found or loaded`,
              severity: 'warning',
            })
            setContainerIDs((ids: any[]) => ids.filter((k) => k !== id))
          }
        },
        onError: (err: AxiosResponse) => setError(err),
      }
    }),
  )
}

export function useBulkUpdateSampleResult(selectedSamples: number[]) {
  const { whoAmI } = useUserContext()
  const { setError, setMessage } = useMessagePopupContext()
  const { setLoadingDialog } = useGlobalContext()
  const queryClient = useQueryClient()
  return useMutation<any, AxiosResponse, string, () => void>(
    (result) => {
      setLoadingDialog(true)
      return SampleService.post(whoAmI!, `/bulkUpdateSampleResults.php`, {
        Result: result,
        SampleIDs: selectedSamples,
      })
    },
    {
      onError: (data) => setError(data),
      onSuccess: async (data) => {
        if (data?.message) {
          setMessage({
            subject: 'Error updating results',
            message: (data as any)?.message,
            severity: 'warning',
          })
        } else {
          await queryClient.invalidateQueries({
            queryKey: ['PhpAPI', '/stuart', '/screen/getScreen.php'],
          })
        }
      },
      onSettled: () => setLoadingDialog(false),
    },
  )
}
